import { Button, Code, Group, Modal, NumberInput, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';

const SECRET_TYPES = [
  { value: 'static', label: 'Static — a fixed value (API key, bearer token, etc.)' },
  { value: 'token_refresh', label: 'Token Refresh — short-lived token obtained via a refresh endpoint' },
];

const DEFAULT_REFRESH_BODY = JSON.stringify(
  {
    refresh_token: '{{refreshToken}}',
  },
  null,
  2
);

const emptyForm = {
  name: '',
  type: 'static',
  value: '',
  refreshUrl: '',
  refreshToken: '',
  refreshBody: DEFAULT_REFRESH_BODY,
  refreshResponseField: '',
  tokenTtlSeconds: 3600,
};

const SecretCreateModal = ({ opened, onClose, onCreated, baseUrl }) => {
  const fetch = useFetch();

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState();

  // Each opening starts from a blank form; a closed Modal keeps its values on
  // screen while it transitions out, so the reset cannot hang off the close.
  useEffect(() => {
    if (opened) {
      setForm(emptyForm);
      setFormError(undefined);
    }
  }, [opened]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = () => {
    setFormError(undefined);

    if (!form.name.trim()) {
      return setFormError('Name is required.');
    }

    if (form.type === 'static') {
      if (!form.value.trim()) {
        return setFormError('Value is required for static secrets.');
      }
    } else if (form.type === 'token_refresh') {
      if (!form.refreshUrl.trim() || !form.refreshToken.trim()) {
        return setFormError('Refresh URL and Token are required for token-refresh secrets.');
      }
      if (!form.refreshUrl.trim().startsWith('https://')) {
        return setFormError('Refresh URL must start with https://');
      }
    }

    let parsedBody;
    try {
      parsedBody = form.type === 'token_refresh' ? JSON.parse(form.refreshBody) : undefined;
    } catch {
      return setFormError('Request Body must be valid JSON.');
    }

    setSaving(true);
    fetch(baseUrl, {
      method: 'POST',
      body: JSON.stringify({
        name: form.name.trim(),
        type: form.type,
        value: form.type === 'static' ? form.value.trim() : undefined,
        refreshUrl: form.type === 'token_refresh' ? form.refreshUrl.trim() : undefined,
        refreshToken: form.type === 'token_refresh' ? form.refreshToken.trim() : undefined,
        refreshBody: form.type === 'token_refresh' ? parsedBody : undefined,
        refreshResponseField: form.type === 'token_refresh' && form.refreshResponseField.trim()
          ? form.refreshResponseField.trim()
          : undefined,
        tokenTtlSeconds: form.type === 'token_refresh' ? form.tokenTtlSeconds : undefined,
      }),
    })
      .then(({ secret }) => {
        onCreated(secret);
        onClose();
      })
      .catch((err) => setFormError(err.message || 'Failed to create secret.'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New Secret" centered size="lg">
      <Stack gap="sm">
        <TextInput
          label="Name"
          description="A unique name to reference this secret in pipelines"
          placeholder="my-api-token"
          value={form.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
        />
        <Select
          label="Type"
          data={SECRET_TYPES}
          value={form.type}
          onChange={(value) => handleFormChange('type', value)}
        />

        {form.type === 'static' && (
          <Textarea
            label="Value"
            description="Stored encrypted at rest. Not shown after creation."
            placeholder="sk-..."
            value={form.value}
            onChange={(e) => handleFormChange('value', e.target.value)}
            autosize
            minRows={2}
          />
        )}

        {form.type === 'token_refresh' && (
          <>
            <TextInput
              label="Refresh URL"
              description="POST endpoint called to obtain a short-lived access token"
              placeholder="https://auth.example.com/token"
              value={form.refreshUrl}
              onChange={(e) => handleFormChange('refreshUrl', e.target.value)}
            />
            <Textarea
              label="Token"
              description="The secret value injected where {{refreshToken}} appears in the body. Stored encrypted, not shown after creation."
              placeholder="ey..."
              value={form.refreshToken}
              onChange={(e) => handleFormChange('refreshToken', e.target.value)}
              autosize
              minRows={2}
            />
            <Textarea
              label="Request Body Template"
              description={
                <>
                  JSON body sent to the Refresh URL. Use <Code>{'{{refreshToken}}'}</Code> where your token should
                  appear, and <Code>{'{{timestamp}}'}</Code> for a current Unix timestamp (seconds).
                </>
              }
              value={form.refreshBody}
              onChange={(e) => handleFormChange('refreshBody', e.target.value)}
              autosize
              minRows={5}
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <TextInput
              label="Response Token Field"
              description='Field name in the response JSON that contains the token. Defaults to "access_token".'
              placeholder="token"
              value={form.refreshResponseField}
              onChange={(e) => handleFormChange('refreshResponseField', e.target.value)}
            />
            <NumberInput
              label="Token TTL (seconds)"
              description='How long the obtained token is valid. Used when the response does not include "expires_in". Default: 3600.'
              value={form.tokenTtlSeconds}
              onChange={(value) => handleFormChange('tokenTtlSeconds', value)}
              min={60}
            />
          </>
        )}

        {formError && (
          <Text c="red" size="sm">
            {formError}
          </Text>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} loading={saving}>
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SecretCreateModal;
